import React, { FC, useState, useRef } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission } from '../../../hooks';
import { useUser } from '../../../hooks/useUser';
import SimpleSamplingTable from './components/SimpleSamplingTable';
import { useSamplingPlanningData, useSamplingPlanningUpdate, useSamplingPlanningUpload, useSamplingPlanningDownload, SamplingPlanningData } from './hooks';

import { StyledContainer, MainContent, TableSection, ActionButton } from './SamplingSettings.styled';
import { UploadButton, ChooseFileButton } from '../../eNO1/eNO1 upload/eNO1Upload.styled';

const SamplingSettings: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [tableData, setTableData] = useState<SamplingPlanningData[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/sampling-planning', '');
  const { data: userData, isLoading: userLoading } = useUser();

  // Fetch sampling planning data
  const { data: samplingData, isLoading: isDataLoading, refetch } = useSamplingPlanningData();
  console.log(samplingData);
  // Update mutation
  const updateMutation = useSamplingPlanningUpdate({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Settings updated successfully',
        severity: 'success',
      });
      refetch();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: `Failed to update settings: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  // Upload mutation
  const uploadMutation = useSamplingPlanningUpload({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success',
      });
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refetch();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: `Failed to upload file: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  // Download mutation
  const downloadMutation = useSamplingPlanningDownload({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Settings downloaded successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: `Failed to download settings: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/sampling-planning');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile);
    }
  };

  const handleTableDataChange = (updatedData: SamplingPlanningData[]) => {
    setTableData(updatedData);
  };

  const handleSaveChanges = () => {
    // Save all table data changes at once
    updateMutation.mutate(tableData);
  };

  const handleSelectAll = () => {
    const updatedData = tableData.map((row) => ({
      ...row,
      performSamplePlanning: true,
    }));
    setTableData(updatedData);
  };

  const handleUnselectAll = () => {
    const updatedData = tableData.map((row) => ({
      ...row,
      performSamplePlanning: false,
    }));
    setTableData(updatedData);
  };

  const resetToDefaultValues = (data: SamplingPlanningData[]): SamplingPlanningData[] => {
    // Define default values for sampling planning fields
    const defaultAccuracyTargets = {
      accuracyTargetPPrio: 7.0,
      accuracyTargetPNonPrio: 7.0,
      accuracyTargetGPrio: 10.0,
      accuracyTargetGNonPrio: 10.0,
      accuracyTargetEPrio: 15.0,
      accuracyTargetENonPrio: 15.0,
      accuracyTargetXPrio: 7.0,
      accuracyTargetXNonPrio: 7.0,
      accuracyTargetTotalPrio: 5.0,
      accuracyTargetTotalNonPrio: 5.0,
    };

    return data.map((row) => ({
      ...row,
      ...defaultAccuracyTargets,
    }));
  };

  const handleResetAllSettings = () => {
    // Reset to default values instead of original API data
    if (samplingData?.content) {
      const defaultData = resetToDefaultValues(samplingData.content);
      setTableData(defaultData);
    }
  };

  const handleDownloadSettings = () => {
    downloadMutation.mutate();
  };

  // Update local table data when fetch data changes
  React.useEffect(() => {
    if (samplingData?.content) {
      setTableData(samplingData.content);
    }
  }, [samplingData]);

  const showLoading = permissionLoading || userLoading || isDataLoading;
  const hasData = samplingData?.content && samplingData.content.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='sampling-settings-container'>
      <Grid container data-testid="sampling-settings-grid-container">
        {/* First Row - SectionBar */}
        <Grid item xs={12} data-testid="sampling-settings-section-bar-container">
          <SectionBar
            title='Sampling Settings'
            subtitle='Sampling Planning'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            data-testid="sampling-settings-section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }} data-testid="sampling-settings-main-row">
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} data-testid="sampling-settings-sidebar-container">
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="sampling-settings-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='sampling-settings-main-content'>
            {showLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="sampling-settings-loading-indicator">
                <CircularProgress data-testid="sampling-settings-loading-spinner" />
              </Box>
            ) : !hasPermission ? (
              <Alert severity='warning' data-testid="sampling-settings-permission-error-alert">You don't have permission to view this section</Alert>
            ) : (
              <StyledContainer data-testid="sampling-settings-container">
                <TableSection data-testid="sampling-settings-section">
                  <Typography variant="h6" sx={{ mb: 2 }} data-testid="sampling-settings-title">
                    Settings
                  </Typography>

                  {/* File Upload Section */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }} data-testid="sampling-settings-upload-section">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      accept=".xlsx,.xls,.csv"
                      data-testid="sampling-settings-file-input"
                    />
                    
                    <ChooseFileButton
                      variant="outlined"
                      onClick={handleChooseFile}
                      startIcon={<CloudUploadIcon />}
                      disabled={uploadMutation.isPending}
                      data-testid="sampling-settings-choose-file-button"
                    >
                      Choose File
                    </ChooseFileButton>

                    <UploadButton
                      variant="contained"
                      onClick={handleUpload}
                      disabled={!selectedFile || uploadMutation.isPending}
                      data-testid="sampling-settings-upload-button"
                    >
                      {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
                    </UploadButton>

                    {selectedFile && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2 }} data-testid="sampling-settings-selected-file-name">
                        {selectedFile.name}
                      </Typography>
                    )}

                    {uploadMutation.isPending && (
                      <CircularProgress size={20} sx={{ ml: 2 }} data-testid="sampling-settings-upload-progress" />
                    )}
                  </Box>

                  {/* Loading State */}
                  {isDataLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }} data-testid="sampling-settings-table-loading">
                      <CircularProgress data-testid="sampling-settings-table-loading-spinner" />
                      <Typography sx={{ ml: 2 }} data-testid="sampling-settings-table-loading-text">Loading sampling planning data...</Typography>
                    </Box>
                  )}

                  {/* Table Section - Only show if there's data */}
                  {!isDataLoading && hasData && (
                    <>
                      <SimpleSamplingTable
                        data={tableData}
                        onDataChange={handleTableDataChange}
                        data-testid="sampling-settings-table"
                      />

                      {/* Action Buttons Below Table */}
                      <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }} data-testid="sampling-settings-action-buttons">
                        <ActionButton
                          variant="contained"
                          onClick={handleSelectAll}
                          disabled={updateMutation.isPending}
                          data-testid="sampling-settings-select-all-button"
                        >
                          Select All
                        </ActionButton>

                        <ActionButton
                          variant="contained"
                          onClick={handleUnselectAll}
                          disabled={updateMutation.isPending}
                          data-testid="sampling-settings-unselect-all-button"
                        >
                          Unselect All
                        </ActionButton>

                        <ActionButton
                          variant="contained"
                          onClick={handleResetAllSettings}
                          disabled={updateMutation.isPending}
                          data-testid="sampling-settings-reset-all-button"
                        >
                          Reset All Settings
                        </ActionButton>

                        <ActionButton
                          variant="contained"
                          onClick={handleSaveChanges}
                          disabled={updateMutation.isPending}
                          data-testid="sampling-settings-save-button"
                        >
                          {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
                        </ActionButton>

                        <ActionButton
                          variant="contained"
                          onClick={handleDownloadSettings}
                          disabled={updateMutation.isPending || downloadMutation.isPending}
                          data-testid="sampling-settings-download-button"
                        >
                          {downloadMutation.isPending ? 'Downloading...' : 'Download SP Settings'}
                        </ActionButton>
                      </Box>
                    </>
                  )}

                  {/* No Data Message */}
                  {!isDataLoading && !hasData && (
                    <Box sx={{ textAlign: 'center', py: 4 }} data-testid="sampling-settings-no-data-message">
                      <Typography variant="body1" color="text.secondary" data-testid="sampling-settings-no-data-text">
                        No sampling planning data available. Please upload a file to get started.
                      </Typography>
                    </Box>
                  )}
                </TableSection>
              </StyledContainer>
            )}

            {/* Notification Snackbar */}
            <NotificationSnackbar
              open={snackbar.open}
              message={snackbar.message}
              severity={snackbar.severity}
              onClose={handleSnackbarClose}
              data-testid="sampling-settings-notification-snackbar"
            />
          </MainContent>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SamplingSettings; 